/**
 * The detail behind each service card.
 *
 * A card on a service page says what the thing is in a sentence. This is what
 * opens when someone clicks it: what the work actually covers, how Altitude
 * runs it, and what the operator ends up holding at the end. It exists
 * because "Operations & SOPs — standard operating procedures and systems"
 * answers none of the three questions a prospect actually has.
 *
 * Keyed by page slug, then by the card's own slug, which is derived from the
 * card title (see `detailSlug`) so adding a card to site.ts does not require
 * touching two files to keep them in step - a card with no entry here simply
 * stays a card.
 */

export interface ServiceDetail {
  /** Longer than the card title, shorter than a headline. */
  heading: string;
  /** One paragraph. The honest version of what this is. */
  lede: string;
  /** How Altitude runs it, in order. These are steps, not features. */
  how: { step: string; detail: string }[];
  /** What the operator is left holding. Deliverables, not promises. */
  deliverables: string[];
  /** Who this is actually for - stated plainly so the wrong fit self-selects
   *  out rather than booking a call that wastes everyone's hour. */
  fitFor: string;
}

/** "Revenue & membership" -> "revenue-membership". Stable enough to live in a
 *  URL, and derived rather than stored so the two files cannot drift. */
export const detailSlug = (title: string): string =>
  title
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const serviceDetails: Record<string, Record<string, ServiceDetail>> = {
  consulting: {
    "revenue-membership": {
      heading: "Revenue and membership",
      lede: "Most membership problems are not demand problems. They are pricing that was set once and never revisited, an offer that does not match the capacity you actually have, and a hand-off between enquiry and first session that loses people quietly.",
      how: [
        { step: "Read the numbers you already have", detail: "Joins and cancellations by month, visits by hour, class fill against capacity, revenue per member, and contribution by service line. Before any recommendation, we establish what is true." },
        { step: "Find the constraint", detail: "Usually one of three: the hours people want are full, the offer converts badly, or the first month loses them. Fixing the wrong one costs a quarter." },
        { step: "Rebuild the offer around capacity", detail: "Pricing tiers with a reason to exist, an intro path with a clear next step, and promotion pointed at the hours you can serve rather than the hours already busy." },
        { step: "Install the measures", detail: "A weekly scorecard with an owner per number, so the change is visible inside a month rather than argued about in a quarter." },
      ],
      deliverables: [
        "A pricing and membership model with the maths behind each tier",
        "A documented path from enquiry to first session to membership decision",
        "A one-page weekly scorecard with named owners",
        "A 12-month revenue plan with the assumptions written down",
      ],
      fitFor: "Operators with steady traffic and flat revenue, or anyone about to raise prices and wanting to know what it will cost them in members.",
    },
    "operations-sops": {
      heading: "Operations and standard procedures",
      lede: "An SOP binder nobody opens is not operations. The work is turning what your best people do instinctively into a sequence a capable new hire can follow on a busy Saturday, and then holding it.",
      how: [
        { step: "Watch the real shifts", detail: "Opening, peak, class changeover, close. We observe before we document, because the written process and the actual one are rarely the same." },
        { step: "Document the five that matter", detail: "The highest-volume journeys: opening and closing, new-member onboarding, class check-in, equipment faults, billing escalations. Each with a trigger, an accountable role, a sequence and proof of completion." },
        { step: "Name ownership per shift", detail: "A shift lead who holds opening checks, escalation, break coverage and the end-of-shift log. Without it, your most conscientious employee silently becomes one." },
        { step: "Train and then audit", detail: "Procedures that are never checked decay in about six weeks. We set the review cadence and the skills matrix alongside the documents." },
      ],
      deliverables: [
        "Written procedures for the five highest-volume workflows",
        "A shift-ownership model and staffing plan against real demand",
        "A skills matrix showing every single point of failure",
        "A review cadence with document owners and revision dates",
      ],
      fitFor: "Clubs where quality depends on who happens to be working, and owners who cannot take a week off without the place wobbling.",
    },
    "growth-expansion": {
      heading: "Growth and expansion",
      lede: "A second location multiplies whatever the first one already does — including the parts that only work because you are standing in the room. Expansion is an operating-maturity question before it is a real-estate question.",
      how: [
        { step: "Test repeatability", detail: "Can lead handling, onboarding, programming, payroll control and reporting run for a month without the owner intervening? If not, the second site inherits the gap twice." },
        { step: "Model the site honestly", detail: "Conservative ramp-up, real labour, occupancy, fit-out and working capital, with a named break-even month rather than a hopeful one." },
        { step: "Write the playbook", detail: "The operating manual the new site opens with, so week one is execution rather than invention." },
        { step: "Prepare the funding case", detail: "The pack a lender or investor actually asks for: unit economics, cohort retention, management depth and the risks stated before they are found." },
      ],
      deliverables: [
        "A readiness assessment against the operating playbook",
        "A location-level financial model with stated assumptions",
        "An opening plan: staffing, pre-sale, programming, week-one checklist",
        "An investor- or lender-ready summary",
      ],
      fitFor: "Operators with one site working well and a second in view, and anyone being asked for numbers by a bank or a partner.",
    },
  },

  management: {
    "club-operations": {
      heading: "Club operations",
      lede: "We run the day-to-day: opening to close, the service standard, the rota, the escalations and the small things members read as evidence that the place is well run.",
      how: [
        { step: "Take the baseline", detail: "Current coverage, service standards, incident history, equipment state and the member complaints that keep recurring." },
        { step: "Set the standard", detail: "A short, observable set of service behaviours — greeting, response time, cleanliness, resolution — that can be coached and reviewed rather than hoped for." },
        { step: "Run it", detail: "Altitude manages the schedule, the shift ownership, the escalation path and the weekly operating review, as your team rather than as a visiting adviser." },
        { step: "Report plainly", detail: "A monthly operating report: what moved, what broke, what was spent and what needs a decision from you." },
      ],
      deliverables: [
        "Daily operating management with named accountability per shift",
        "A documented service standard and coaching rhythm",
        "Incident, maintenance and escalation logs you can audit",
        "A monthly operating report written for an owner, not a committee",
      ],
      fitFor: "Owners who want the club run properly without being the one running it, and investors holding a site that needs an operator.",
    },
    "people-hr": {
      heading: "People and HR",
      lede: "Great trainers are the product. Most clubs lose them to the same three things: an unclear role, a schedule published too late, and no path beyond the floor.",
      how: [
        { step: "Define the roles", detail: "What each role owns, what good looks like, and how performance is actually observed rather than assumed." },
        { step: "Fix hiring lead times", detail: "Work backwards from when someone needs to be useful. Posting when you notice the gap is already four to six weeks late." },
        { step: "Onboard with purpose", detail: "A structured first 30 days so a new coach is productive and confident rather than shadowing until someone has time." },
        { step: "Build the ladder", detail: "Progression, continuing education and a reason to stay that is not purely money." },
      ],
      deliverables: [
        "Role definitions, comp structure and a performance framework",
        "A hiring pipeline with realistic lead times",
        "A 30-day onboarding programme per role",
        "A skills matrix, training calendar and retention plan",
      ],
      fitFor: "Clubs replacing the same role twice a year, and operators whose payroll is rising without output rising with it.",
    },
    facilities: {
      heading: "Facilities",
      lede: "Members read the state of the room as the state of the business. Equipment downtime costs far more in trust than it does in parts.",
      how: [
        { step: "Build the register", detail: "Every asset, its service interval, its warranty position and its parts lead time. Most clubs discover here that they own machines nobody services." },
        { step: "Set the rounds", detail: "Daily, weekly and monthly checks mapped to peak traffic, with cleaning and reset standards attached to the rooms that need them most." },
        { step: "Hold the vendors", detail: "Response times, scope boundaries and escalation written into the relationship, then reviewed against what actually happened." },
        { step: "Plan the capital", detail: "A replacement schedule so equipment spend is a plan rather than an emergency." },
      ],
      deliverables: [
        "An equipment and maintenance register with ownership",
        "Inspection and cleaning schedules tied to traffic",
        "Vendor scorecards and escalation paths",
        "A multi-year capital replacement plan",
      ],
      fitFor: "Facilities where something is always out of service, and operators planning a refit who want the sequence right.",
    },
    business: {
      heading: "Business",
      lede: "Revenue, marketing and membership growth, connected to the operating plan rather than run as a separate campaign that the floor finds out about on the day.",
      how: [
        { step: "Connect demand to capacity", detail: "Market into the hours and services you can actually deliver, so growth does not arrive as a service failure." },
        { step: "Own the funnel end to end", detail: "Source, enquiry, consultation, show rate, conversion, first 30 days — one owner, one scoreboard." },
        { step: "Price deliberately", detail: "Annual review against cost-to-serve, capacity and segment, communicated plainly rather than quietly." },
        { step: "Review monthly", detail: "What was spent, what it returned, and what stops next month." },
      ],
      deliverables: [
        "A marketing and membership plan matched to capacity",
        "Lead tracking by source through to conversion",
        "A pricing review with the cost-to-serve maths",
        "A monthly growth report",
      ],
      fitFor: "Operators spending on marketing without knowing which half works, and clubs whose busy hours are full while the rest sit empty.",
    },
    financial: {
      heading: "Financial",
      lede: "Bookkeeping, reporting, collections and advisory, delivered with Northgate Strategic Partners so the accounting and the operating plan are reading the same numbers.",
      how: [
        { step: "Get the books current", detail: "Clean, timely bookkeeping is the precondition for every other decision on this page." },
        { step: "Report what decides things", detail: "Revenue per member, labour as a share of revenue, contribution by service line, and cash position — monthly, not annually." },
        { step: "Protect the cash", detail: "A collections cadence on past-due accounts, with arrangements where they make sense and escalation where they do not." },
        { step: "Plan ahead", detail: "Tax planning, capital decisions and the funding conversation, prepared before they are urgent." },
      ],
      deliverables: [
        "Monthly bookkeeping and a management report pack",
        "A collections process with defined stages",
        "Tax planning and preparation through Northgate",
        "Budget, forecast and capital planning support",
      ],
      fitFor: "Owners who find out how the year went in March, and clubs carrying receivables nobody is chasing.",
    },
    strategy: {
      heading: "Strategy",
      lede: "Planning and oversight across the whole operation: where the business is going, what it will take, and which decisions this year actually matter.",
      how: [
        { step: "Agree the destination", detail: "Hold and improve, grow the site, add a location, or prepare to sell. Each implies a different operating plan, and most clubs are quietly pursuing two at once." },
        { step: "Set the plan", detail: "A 12-month plan with quarterly priorities, named owners and the measures that show progress." },
        { step: "Govern it", detail: "A monthly leadership review on financial performance, capacity, staffing and the next investment decision." },
        { step: "Adjust openly", detail: "Plans meet reality. The review exists to change the plan on evidence rather than to defend it." },
      ],
      deliverables: [
        "A 12-month operating plan with quarterly priorities",
        "A leadership reporting pack and meeting cadence",
        "Investment and capital decision framework",
        "Annual review and re-plan",
      ],
      fitFor: "Owner-operators carrying every decision alone, and ownership groups needing a management layer they can hold to account.",
    },
  },

  "business-solutions": {
    "facility-services": {
      heading: "Facility services",
      lede: "Design, build-out, equipment and maintenance, run as one thread so the people who drew the room are accountable for how it operates once it is full.",
      how: [
        { step: "Programme and design", detail: "Zoning driven by what you teach and how many people are in the room at peak, drawn professionally and coordinated with services." },
        { step: "Build and install", detail: "Qualified contractors bidding a clear scope, a schedule built backwards from opening, and one point of accountability for the site." },
        { step: "Equip deliberately", detail: "Specified from the programme and compared on total cost of ownership, not list price." },
        { step: "Keep it running", detail: "An asset register, preventive schedule and vendor standards handed over with the building." },
      ],
      deliverables: ["Design drawings and equipment layout", "Construction management through to close-out", "Equipment schedule and service agreements", "A maintenance programme your team can run"],
      fitFor: "Anyone building, relocating or refitting — and operators whose current room forces staffing it should not need.",
    },
    "business-services": {
      heading: "Business services",
      lede: "The commercial engine: what you charge, who you are speaking to, how an enquiry becomes a member, and the procedures that keep service the same on a Tuesday and a Saturday.",
      how: [
        { step: "Set the baseline", detail: "Revenue per member, contribution by service line, retention by cohort, capacity by hour." },
        { step: "Fix pricing and offer", detail: "A small number of tiers with a defensible reason to exist, matched to the capacity you have." },
        { step: "Build the funnel", detail: "One audience, one promise, and a tracked path from enquiry to first session to decision." },
        { step: "Document the operation", detail: "The procedures and checklists that make the member experience repeatable." },
      ],
      deliverables: ["A pricing and membership model", "A local marketing plan with lead-source tracking", "An SOP set for core operations", "A weekly scorecard with owners"],
      fitFor: "Operators whose traffic is fine and whose revenue is flat, and anyone preparing to scale what currently works by instinct.",
    },
    "financial-services": {
      heading: "Financial services",
      lede: "Bookkeeping, reporting, tax and receivables, delivered with Northgate Strategic Partners so the accounting and the operating plan read the same numbers.",
      how: [
        { step: "Get current", detail: "A clean chart of accounts for a fitness business and a real monthly close." },
        { step: "Report what decides things", detail: "Revenue per member, labour share, contribution and cash — monthly, in a form an owner can act on." },
        { step: "Protect cash", detail: "A defined collections ladder with arrangements where they make sense and escalation where they do not." },
        { step: "Plan ahead", detail: "Tax planning, budgets and the funding conversation prepared before they are urgent." },
      ],
      deliverables: ["Monthly bookkeeping and a management pack", "A collections process", "Tax planning and preparation", "Budget and forecast support"],
      fitFor: "Owners running the business from a bank balance, and clubs carrying receivables nobody is chasing.",
    },
    "management-services": {
      heading: "Management services",
      lede: "Altitude runs the operation: shifts, standards, hiring, training and the reporting that tells you it is being held without you standing in the room.",
      how: [
        { step: "Baseline the operation", detail: "Coverage, standards, incidents, equipment and recurring complaints." },
        { step: "Set observable standards", detail: "Service behaviours specific enough to coach and review." },
        { step: "Run the rhythm", detail: "Rotas, shift ownership, escalation and the weekly operating review." },
        { step: "Report to the owner", detail: "A monthly report on what moved, what broke and what needs your decision." },
      ],
      deliverables: ["Daily operating management", "Hiring, onboarding and training programmes", "Incident and maintenance logs", "A monthly operating report"],
      fitFor: "Owners who want the club held to a standard without holding it themselves, and investors needing an operator.",
    },
  },

  "personal-training": {
    "for-clients": {
      heading: "For clients",
      lede: "Finding a trainer by scrolling profiles is how people end up with someone who is excellent for somebody else. We start with your situation and match from there.",
      how: [
        { step: "Consultation", detail: "What you want, what has worked before, what your week actually allows, and any constraint a programme has to respect." },
        { step: "Assessment", detail: "A grounded movement and fitness baseline, explained in plain language, that gives the training somewhere to start." },
        { step: "Match", detail: "A coach whose approach, specialism and schedule fit yours — not whoever has a gap." },
        { step: "Start and review", detail: "First sessions, then a check-in on whether the fit and the plan are right. Changing early is normal and cheap." },
      ],
      deliverables: ["A consultation and movement assessment", "A recommended trainer with the reasoning", "A starting programme and realistic cadence", "A check-in after the first block"],
      fitFor: "Anyone in New York City who has tried training before and wants it to stick this time, and beginners who would rather not guess.",
    },
    "for-coaches": {
      heading: "For coaches",
      lede: "Good coaches lose time to everything that is not coaching: finding clients, chasing payments, insurance, admin. The network exists to take that back.",
      how: [
        { step: "Apply and verify", detail: "Credentials, insurance and experience checked, because the match is only worth something if the standard is real." },
        { step: "Get matched", detail: "Clients introduced on the basis of fit — goals, style, schedule, location — rather than volume." },
        { step: "Use the back office", detail: "Scheduling, payment and the admin that otherwise eats an evening a week." },
        { step: "Keep developing", detail: "Continuing education, insurance resources and peer standards." },
      ],
      deliverables: ["A verified profile in the Altitude network", "Client introductions matched to your practice", "Scheduling and payment tooling", "CE and insurance resources"],
      fitFor: "Independent trainers building a book, and coaches leaving a big-box floor who want to keep the clients and lose the admin.",
    },
    "for-operators": {
      heading: "For operators",
      lede: "A personal training offer inside a club is a margin line and a retention engine, and most are neither because nobody owns the programme.",
      how: [
        { step: "Design the offer", detail: "Packages, pricing, trainer split and capacity that work for the club and the coach." },
        { step: "Staff it", detail: "Hiring standards, onboarding and the coaching cadence that keeps quality even." },
        { step: "Integrate it", detail: "Intro sessions wired into membership onboarding, so PT is a path rather than an upsell." },
        { step: "Measure it", detail: "Attach rate, utilisation, revenue per coach hour and retention of PT clients against the rest." },
      ],
      deliverables: ["A PT offer and pricing model", "Hiring and onboarding standards for coaches", "An integration plan with member onboarding", "A PT scorecard"],
      fitFor: "Clubs whose PT revenue depends on one or two individuals, and operators adding training for the first time.",
    },
  },

  clients: {
    consult: {
      heading: "Consult",
      lede: "A conversation before a programme. What you want, what you have tried, and what your week realistically allows — because a plan built for a week you do not have is the usual reason training stops in March.",
      how: [
        { step: "Talk about the goal", detail: "Not just the outcome, but what reaching it would change. Specific goals produce specific programmes." },
        { step: "Map the constraints", detail: "Schedule, commute, previous injuries, equipment access, and what you actively dislike doing." },
        { step: "Set expectations", detail: "Honest timelines, realistic session frequency and what progress looks like in the first six weeks." },
        { step: "Agree the next step", detail: "Assessment, a trainer introduction, or a recommendation that we are not the right fit." },
      ],
      deliverables: ["A documented goal and starting point", "An honest timeline", "A recommended path", "No obligation to continue"],
      fitFor: "Anyone who has started before and stopped, and beginners who would rather be guided than guess.",
    },
    evaluate: {
      heading: "Evaluate",
      lede: "A grounded baseline so the training has somewhere to start — and so progress is measurable in something other than how you feel on the day.",
      how: [
        { step: "Movement screening", detail: "How you move now, including anything that needs working around rather than through." },
        { step: "Baseline measures", detail: "Relevant to your goal: strength, capacity, mobility or consistency. Not every measure matters to every person." },
        { step: "Explain it plainly", detail: "What the results mean in language you can use, not a printout you file away." },
        { step: "Set the first targets", detail: "Two or three things that should move in six weeks." },
      ],
      deliverables: ["A movement and fitness baseline", "A plain-language explanation", "Two or three six-week targets", "A record to measure against later"],
      fitFor: "Anyone starting, restarting, or training consistently without knowing whether it is working.",
    },
    match: {
      heading: "Match",
      lede: "Trainers are not interchangeable. The best coach for a competitive lifter is often the wrong coach for someone nervous about walking onto a gym floor.",
      how: [
        { step: "Weigh the fit", detail: "Goals, coaching style, specialism, personality, schedule and location — in that order of importance." },
        { step: "Check the standard", detail: "Credentials, insurance and experience verified before anyone is introduced." },
        { step: "Introduce properly", detail: "The coach arrives knowing your goals and your assessment, so session one is training rather than interviewing." },
        { step: "Review the fit", detail: "A check-in after the first block. Changing early is normal; we would rather move you than lose you." },
      ],
      deliverables: ["A matched trainer with the reasoning explained", "Verified credentials and insurance", "A briefed first session", "A fit review after the first block"],
      fitFor: "People in New York City who want the right coach rather than the nearest available one.",
    },
    train: {
      heading: "Train",
      lede: "The part that only works through repetition. The structure around it exists to make showing up the easy choice on the week you least feel like it.",
      how: [
        { step: "Programme properly", detail: "Progression planned in blocks rather than improvised each session." },
        { step: "Build the habit", detail: "A schedule you can actually hold, with the minimum viable week defined for the bad ones." },
        { step: "Track what matters", detail: "Attendance, loads, technique and how you feel — reviewed rather than just recorded." },
        { step: "Adjust openly", detail: "Life changes. The programme changes with it rather than quietly failing." },
      ],
      deliverables: ["A structured training programme", "A realistic weekly schedule", "Progress tracking you can see", "Regular review and adjustment"],
      fitFor: "Anyone who wants training to still be happening in six months.",
    },
  },

  coaches: {
    "new-client-opportunities": {
      heading: "New client opportunities",
      lede: "Finding clients is the part of independent coaching that has nothing to do with coaching, and it is where most good trainers lose their evenings.",
      how: [
        { step: "Build your profile", detail: "Specialism, style, availability and the kind of client you do your best work with." },
        { step: "Get matched on fit", detail: "Introductions based on what the client needs and how you coach, not on who is next in a queue." },
        { step: "Meet prepared", detail: "You receive the client's goals and assessment before the first session." },
        { step: "Keep the relationship", detail: "The client is yours. Altitude introduces and supports; it does not sit between you forever." },
      ],
      deliverables: ["A profile in the Altitude network", "Matched client introductions", "Client context before session one", "A direct coaching relationship"],
      fitFor: "Independent trainers building a book, and coaches leaving a big-box floor.",
    },
    "certifications-ce": {
      heading: "Certifications and CE",
      lede: "Continuing education is a requirement and an expense, and most coaches do whatever is cheapest before the deadline rather than what would make them better.",
      how: [
        { step: "Map your requirements", detail: "What your certifying body needs, by when, and what you already have." },
        { step: "Choose deliberately", detail: "Education matched to the clients you want, so the credit and the capability are the same spend." },
        { step: "Use the network", detail: "Group access and shared resources where they exist." },
        { step: "Keep the record", detail: "Credentials and renewal dates tracked, so a lapse never costs you work." },
      ],
      deliverables: ["A CE plan against your renewal dates", "Access to network resources", "Credential tracking", "Recommendations matched to your practice"],
      fitFor: "Coaches who want their CE spend to do two jobs at once.",
    },
    "trainer-insurance": {
      heading: "Trainer insurance",
      lede: "Liability cover is the thing independent coaches most often carry badly or not at all, and the gap is invisible right up until it is not.",
      how: [
        { step: "Understand the exposure", detail: "What you do, where you do it, and what that actually exposes you to." },
        { step: "Review the cover", detail: "Liability limits, professional indemnity, and where facility cover ends and yours begins." },
        { step: "Get it in place", detail: "Resources and routes designed for independent fitness professionals rather than for clubs." },
        { step: "Keep it current", detail: "Renewal tracked alongside your credentials, because a lapse in either stops you working." },
      ],
      deliverables: ["A review of your current cover", "Insurance resources for independents", "Clarity on facility versus personal cover", "Renewal tracking"],
      fitFor: "Independent and mobile trainers, and anyone coaching across more than one facility.",
    },
  },

  "free-blueprint": {
    "top-growth-opportunities": {
      heading: "Top growth opportunities",
      lede: "Not a list of everything that could be improved — the two or three moves most likely to produce something visible inside a quarter, given where your business actually is.",
      how: [
        { step: "Read your answers", detail: "Stage, size, revenue band, team size and the challenges you named set the realistic range of moves." },
        { step: "Rule out the wrong ones", detail: "Most advice fails because it was written for a different size of business. We cut what does not apply to yours." },
        { step: "Rank what is left", detail: "By effect, by effort and by how soon you would know whether it worked." },
        { step: "Make it specific", detail: "Each one written as something a person can start on Monday, not as a principle." },
      ],
      deliverables: ["Two to three ranked opportunities", "The reasoning behind each", "A first action for each one", "All of it inside a few minutes of submitting"],
      fitFor: "Owners who know something needs to change and want a second opinion before committing money to it.",
    },
    "single-biggest-bottleneck": {
      heading: "Single biggest bottleneck",
      lede: "Most fitness businesses have one constraint doing the damage, and improving everything around it changes almost nothing.",
      how: [
        { step: "Compare the symptoms", detail: "What you reported against the pattern it usually belongs to — capacity, conversion, retention, staffing or cash." },
        { step: "Name it plainly", detail: "One constraint, stated in a sentence you would recognise as true." },
        { step: "Say what it is costing", detail: "In the terms that matter to you: members, hours, margin or your own week." },
        { step: "Give the first move", detail: "The cheapest action that would tell you whether the diagnosis is right." },
      ],
      deliverables: ["A named constraint", "What it is costing you", "The first test to run", "What to leave alone for now"],
      fitFor: "Anyone doing five things at once and seeing none of them move.",
    },
    "12-month-roadmap": {
      heading: "12-month roadmap",
      lede: "A sequence rather than a wish list: what to fix first because everything else depends on it, and what can safely wait two quarters.",
      how: [
        { step: "Sequence by dependency", detail: "Retention before acquisition, capacity before promotion, systems before a second site. Order is most of the value." },
        { step: "Set the quarters", detail: "One priority per quarter with a measure attached, because four priorities is none." },
        { step: "Attach owners", detail: "Even in a small team, a priority with no name against it does not happen." },
        { step: "Define done", detail: "What has to be true at each review for the next step to start." },
      ],
      deliverables: ["A four-quarter sequence", "One measurable priority per quarter", "Owners and review points", "Clear criteria for moving on"],
      fitFor: "Operators planning next year, and anyone preparing to raise money or open a second location.",
    },
  },

  "facility-services": {
    design: {
      heading: "Design",
      lede: "Club design decides your labour model, your sightlines, your peak-hour capacity and your maintenance bill for the next decade. It is an operating decision that happens to be drawn.",
      how: [
        { step: "Programme first", detail: "What happens in the space, at what hours, for how many people. Zoning follows programming; done the other way round you get a beautiful room that cannot hold a 6pm class." },
        { step: "Plan the flows", detail: "Entry, check-in, changing, floor, studios, recovery, staff. Member journey and staff sightlines are designed together so one person can cover a quiet shift safely." },
        { step: "Draw it properly", detail: "Professional drawings coordinated with equipment specification, power, ventilation, drainage and acoustics, so the build has no surprises in it." },
        { step: "Test against operations", detail: "We run the plan against peak-hour volume, cleaning rounds and class changeover before anyone orders materials." },
      ],
      deliverables: [
        "Programming and zoning plan with capacity per zone",
        "Professional design drawings and equipment layout",
        "Coordinated MEP, acoustic and finish specifications",
        "An operating review of the design before build",
      ],
      fitFor: "New builds, relocations and major refits — and anyone whose current layout forces staffing they should not need.",
    },
    "build-out": {
      heading: "Build-out",
      lede: "Fitness build-outs fail on sequencing and on scope ambiguity, not on craftsmanship. The work is holding contractors to a schedule and a scope that were clear before anyone started.",
      how: [
        { step: "Tender properly", detail: "Qualified fitness-experienced contractors bidding the same scope, with references checked and exclusions read rather than skimmed." },
        { step: "Lock the schedule", detail: "Long-lead equipment, inspections, utility work and seasonality mapped backwards from the opening date." },
        { step: "Manage the site", detail: "Weekly coordination, RFI and change-order control, and a single point of accountability so questions do not sit for a week." },
        { step: "Close it out", detail: "Punch list, commissioning, warranties, as-builts and O&M documents handed over in a form your team can actually use." },
      ],
      deliverables: [
        "Tender package and contractor recommendation",
        "A construction schedule with critical path and long-lead items",
        "Weekly progress reporting and change-order control",
        "Full close-out pack: warranties, as-builts, O&M manuals",
      ],
      fitFor: "Operators building or renovating who do not want to be the general contractor by accident.",
    },
    equipment: {
      heading: "Equipment",
      lede: "Equipment is specified for the programme and the people, not for the catalogue. The second question after what to buy is always who services it and how fast.",
      how: [
        { step: "Specify from the programme", detail: "Mix, quantity and spacing driven by what you teach and how many people are in the room at 6pm." },
        { step: "Compare on total cost", detail: "Purchase price, service contract, consumables, warranty terms and parts availability — the cheapest unit is frequently the most expensive asset." },
        { step: "Coordinate the install", detail: "Delivery sequencing against construction, power and flooring, so nothing arrives before the room is ready to take it." },
        { step: "Hand over trained", detail: "Staff trained on operation, basic fault-finding and the escalation path before day one." },
      ],
      deliverables: [
        "An equipment schedule tied to the layout and programme",
        "Vendor comparison on total cost of ownership",
        "Delivery and installation sequencing plan",
        "Service agreements and staff handover training",
      ],
      fitFor: "New facilities, refits and replacement cycles — particularly where floor space is tight and every metre has to earn.",
    },
    maintenance: {
      heading: "Maintenance",
      lede: "Planned maintenance is cheaper than reactive maintenance by a margin most operators only discover after a year of each. The difference is a register and a rhythm.",
      how: [
        { step: "Register the assets", detail: "What you own, its age, its service interval, its warranty and its parts lead time." },
        { step: "Schedule the work", detail: "Preventive intervals set around traffic rather than around the calendar, with the quiet hours used deliberately." },
        { step: "Route the faults", detail: "One reporting route, one owner, defined response times, and a log that shows what is still open." },
        { step: "Review and replace", detail: "Quarterly review of downtime and cost per asset, feeding a replacement plan rather than a crisis." },
      ],
      deliverables: [
        "A complete asset and maintenance register",
        "Preventive maintenance schedule and daily checks",
        "Fault reporting route with response standards",
        "Quarterly downtime review and replacement plan",
      ],
      fitFor: "Any facility where members notice breakdowns before management does.",
    },
  },

  "business-services": {
    "revenue-membership": {
      heading: "Revenue and membership",
      lede: "Pricing, offers, acquisition, retention and service mix, treated as one system — because changing any of them in isolation moves the others whether you planned it or not.",
      how: [
        { step: "Establish the baseline", detail: "Revenue per member, contribution by service line, cohort retention and capacity utilisation." },
        { step: "Rebuild the tiers", detail: "A small number of understandable options, each with a reason to exist and a defensible price." },
        { step: "Fix the first month", detail: "The onboarding that decides whether a January joiner is still there in April." },
        { step: "Measure weekly", detail: "Leads, show rate, conversion, net growth, attendance — reviewed while there is still time to act." },
      ],
      deliverables: [
        "Pricing and membership structure with supporting maths",
        "Onboarding programme for the first 30 days",
        "A weekly scorecard with owners",
        "A 12-month revenue plan",
      ],
      fitFor: "Studios and clubs whose membership is flat while footfall is not.",
    },
    marketing: {
      heading: "Marketing",
      lede: "A practical local marketing engine: one audience, one promise, and a path from attention to a booked intro that does not leak at every step.",
      how: [
        { step: "Define one audience", detail: "The specific person within a realistic travel distance, and the reason they should choose you over the three nearer options." },
        { step: "Fix the shop window", detail: "Site, local search listing, reviews and photography — most lost enquiries never reach a form." },
        { step: "Build one reliable path", detail: "Low-friction intro offer, fast follow-up, clear next step, and a booking process that works on a phone." },
        { step: "Track to conversion", detail: "By source, through to members — so spend follows what converts rather than what generates clicks." },
      ],
      deliverables: [
        "Positioning and audience definition",
        "Local search and review strategy",
        "An intro offer and follow-up sequence",
        "Lead-source reporting through to conversion",
      ],
      fitFor: "Operators spending on ads without attribution, and anyone opening in a competitive catchment.",
    },
    "operations-sops": {
      heading: "Operations and SOPs",
      lede: "The written version of how your business runs, at the level of detail a capable new employee can act on during a busy shift.",
      how: [
        { step: "Map the journeys", detail: "The workflows that happen most, not the ones that are most interesting to document." },
        { step: "Write for the floor", detail: "Trigger, owner, sequence, escalation, proof of completion. Photographs where a visual standard matters." },
        { step: "Assign and date", detail: "Every document has an owner and a review date, or it is fiction within two months." },
        { step: "Audit lightly", detail: "A short observation cadence that catches drift before members do." },
      ],
      deliverables: [
        "A documented SOP set for core operations",
        "Shift checklists and escalation paths",
        "An owner and review date per document",
        "An audit rhythm and scorecard",
      ],
      fitFor: "Growing teams, second locations, and any club where consistency depends on tenure.",
    },
  },

  "financial-services": {
    accounting: {
      heading: "Accounting",
      lede: "Bookkeeping and reporting that make the next decision clearer, delivered with Northgate Strategic Partners.",
      how: [
        { step: "Clean up and connect", detail: "Chart of accounts built for a fitness business, bank and merchant feeds connected, historic mess reconciled." },
        { step: "Close monthly", detail: "A real monthly close on a schedule, so the numbers are current enough to act on." },
        { step: "Report usefully", detail: "Revenue per member, labour share, contribution by service line and cash position — not just a P&L." },
        { step: "Review together", detail: "A monthly conversation about what the numbers imply, which is the part that changes outcomes." },
      ],
      deliverables: [
        "A fitness-specific chart of accounts",
        "Monthly bookkeeping and close",
        "A management reporting pack",
        "A monthly review with an accountable adviser",
      ],
      fitFor: "Operators running the business from a bank balance, and anyone preparing for finance or sale.",
    },
    tax: {
      heading: "Tax",
      lede: "Tax planning and preparation through Northgate Strategic Partners, handled with the operating plan in view rather than in a March scramble.",
      how: [
        { step: "Review the structure", detail: "Entity structure, owner compensation and how the business is actually organised against how it is taxed." },
        { step: "Plan before year end", detail: "Capital purchases, timing and elections decided while they can still change the outcome." },
        { step: "Prepare and file", detail: "Business and related filings prepared by licensed professionals." },
        { step: "Keep it current", detail: "Quarterly check-ins so nothing is discovered at the deadline." },
      ],
      deliverables: [
        "Entity and compensation structure review",
        "A year-end tax plan",
        "Business return preparation and filing",
        "Quarterly planning check-ins",
      ],
      fitFor: "Fitness businesses whose tax work is currently separate from everything else about the business.",
    },
    collections: {
      heading: "Collections",
      lede: "Receivables work that protects cash flow and the relationship at the same time — a defined ladder rather than an awkward phone call when it is already too late.",
      how: [
        { step: "Age the book", detail: "What is outstanding, how old, and which accounts are worth working." },
        { step: "Run the ladder", detail: "Scheduled reminders at defined day marks, each one professional, documented and consistent." },
        { step: "Offer arrangements", detail: "Payment arrangements where they are realistic, recorded with terms and an end date." },
        { step: "Escalate deliberately", detail: "Accounts that pass the agreed threshold are referred, as a decision someone made rather than a step that happened." },
      ],
      deliverables: [
        "An aged receivables review",
        "A documented collections ladder",
        "Arrangement terms and records",
        "Referral criteria and escalation path",
      ],
      fitFor: "Clubs carrying months of unpaid memberships or unpaid commercial accounts.",
    },
    advisory: {
      heading: "Advisory",
      lede: "The decisions that do not fit in a monthly report: whether to buy the building, take the partner, add the location, or sell.",
      how: [
        { step: "Frame the decision", detail: "What is actually being decided, by when, and what would have to be true for each option to be right." },
        { step: "Model the options", detail: "Cash, risk and operating impact modelled side by side rather than argued in the abstract." },
        { step: "Stress-test", detail: "The downside case written down before the decision, not discovered after it." },
        { step: "Support the execution", detail: "Diligence, lender or buyer conversations, and the operating plan that follows." },
      ],
      deliverables: [
        "A decision framework and options analysis",
        "Financial models with downside cases",
        "Diligence and documentation support",
        "An implementation plan for the chosen path",
      ],
      fitFor: "Owners facing a one-off decision big enough that getting it wrong is expensive.",
    },
  },

  "management-services": {
    "club-operations": {
      heading: "Club operations",
      lede: "Day-to-day management and the service standards that define your brand, run by Altitude as part of your team.",
      how: [
        { step: "Baseline the operation", detail: "Coverage, standards, incidents, equipment and the complaints that keep coming back." },
        { step: "Set observable standards", detail: "Service behaviours specific enough to coach and review." },
        { step: "Manage the rhythm", detail: "Rotas, shift ownership, escalation and the weekly operating review." },
        { step: "Report to the owner", detail: "A monthly report on what moved, what broke and what needs a decision." },
      ],
      deliverables: [
        "Daily operating management",
        "A documented service standard",
        "Incident and maintenance logs",
        "A monthly operating report",
      ],
      fitFor: "Owners who want the operation held to a standard without holding it themselves.",
    },
    "people-hr": {
      heading: "People and HR",
      lede: "Hiring, HR management, training and retention — the systems that decide whether good coaches stay long enough to matter to members.",
      how: [
        { step: "Define roles and pay", detail: "Clear ownership and a comp structure that survives comparison between staff." },
        { step: "Hire on lead time", detail: "A pipeline run backwards from when someone must be useful." },
        { step: "Onboard structurally", detail: "A first 30 days that produces a productive coach rather than a shadowed one." },
        { step: "Develop and retain", detail: "Training calendar, progression and a review cadence people take seriously." },
      ],
      deliverables: [
        "Role definitions and comp structure",
        "A hiring and onboarding programme",
        "A training calendar and skills matrix",
        "A performance and retention framework",
      ],
      fitFor: "Clubs with turnover they cannot explain and owners doing HR in the evenings.",
    },
    oversight: {
      heading: "Oversight",
      lede: "Performance systems and management support that build accountability — so standards hold when nobody senior is in the building.",
      how: [
        { step: "Decide what is measured", detail: "A short set of measures that actually describe the operation, each with an owner." },
        { step: "Observe, do not assume", detail: "Scheduled observation of service, safety and facility standards, logged." },
        { step: "Review on a cadence", detail: "Weekly operating review, monthly management review, with decisions recorded." },
        { step: "Close the loop", detail: "Corrective actions tracked to completion rather than raised and forgotten." },
      ],
      deliverables: [
        "An operating scorecard with owners",
        "A scheduled observation and audit programme",
        "A meeting cadence with recorded decisions",
        "Corrective action tracking",
      ],
      fitFor: "Multi-site operators and ownership groups who need to know what is happening without being there.",
    },
  },
};

/** The detail for a card, if one has been written. */
export const detailFor = (pageSlug: string, cardTitle: string): ServiceDetail | undefined =>
  serviceDetails[pageSlug]?.[detailSlug(cardTitle)];
